/**
 * Workshop evaluation tags — single source of truth for IDs used in AI JSON and UI colors.
 * Used for solutions, impact-path nodes, lens items, and other AI-evaluated user content.
 * Human-readable labels live in apps/display locales (evaluationTags.*).
 */

/** @typedef {'strength' | 'beyond'} EvaluationTagCategory */

/** @type {Record<string, { category: EvaluationTagCategory, color: string, bgLight: string, borderLight: string, darkBg: string, darkBorder: string }>} */
export const EVALUATION_TAG_META = {
  ambitious: {
    category: 'strength',
    color: '#7c3aed',
    bgLight: '#f5f3ff',
    borderLight: '#ddd6fe',
    darkBg: 'rgba(124, 58, 237, 0.12)',
    darkBorder: 'rgba(124, 58, 237, 0.35)',
  },
  feasible: {
    category: 'strength',
    color: '#00613d',
    bgLight: '#ecfdf5',
    borderLight: '#a7f3d0',
    darkBg: 'rgba(5, 150, 105, 0.12)',
    darkBorder: 'rgba(5, 150, 105, 0.35)',
  },
  innovative: {
    category: 'strength',
    color: '#367ABA',
    bgLight: '#f5f5f5',
    borderLight: '#bfdbfe',
    darkBg: 'rgba(54, 122, 186, 0.12)',
    darkBorder: 'rgba(54, 122, 186, 0.35)',
  },
  creative: {
    category: 'strength',
    color: '#0891b2',
    bgLight: '#ecfeff',
    borderLight: '#a5f3fc',
    darkBg: 'rgba(8, 145, 178, 0.12)',
    darkBorder: 'rgba(8, 145, 178, 0.35)',
  },
  research_backed: {
    category: 'strength',
    color: '#7a3a0c',
    bgLight: '#fffbeb',
    borderLight: '#fde68a',
    darkBg: 'rgba(180, 83, 9, 0.12)',
    darkBorder: 'rgba(180, 83, 9, 0.35)',
  },
  systemic: {
    category: 'strength',
    color: '#6d28d9',
    bgLight: '#faf5ff',
    borderLight: '#e9d5ff',
    darkBg: 'rgba(109, 40, 217, 0.12)',
    darkBorder: 'rgba(109, 40, 217, 0.35)',
  },
  tested_elsewhere: {
    category: 'strength',
    color: '#0f766e',
    bgLight: '#f0fdfa',
    borderLight: '#99f6e4',
    darkBg: 'rgba(15, 118, 110, 0.12)',
    darkBorder: 'rgba(15, 118, 110, 0.35)',
  },
  quick_to_implement: {
    category: 'strength',
    color: '#15803d',
    bgLight: '#f7fee7',
    borderLight: '#bef264',
    darkBg: 'rgba(21, 128, 61, 0.12)',
    darkBorder: 'rgba(21, 128, 61, 0.35)',
  },
  uncharted: {
    category: 'beyond',
    color: '#a855f7',
    bgLight: '#faf5ff',
    borderLight: '#e9d5ff',
    darkBg: 'rgba(168, 85, 247, 0.14)',
    darkBorder: 'rgba(168, 85, 247, 0.4)',
  },
  expert_insight: {
    category: 'beyond',
    color: '#c026d3',
    bgLight: '#fdf4ff',
    borderLight: '#f0abfc',
    darkBg: 'rgba(192, 38, 211, 0.14)',
    darkBorder: 'rgba(192, 38, 211, 0.4)',
  },
  bold_hypothesis: {
    category: 'beyond',
    color: '#ea580c',
    bgLight: '#fff7ed',
    borderLight: '#fdba74',
    darkBg: 'rgba(234, 88, 12, 0.14)',
    darkBorder: 'rgba(234, 88, 12, 0.4)',
  },
};

export const EVALUATION_TAG_IDS = Object.keys(EVALUATION_TAG_META);

/** Finnish labels for UI / prompts (fi workshops). */
export const EVALUATION_TAG_LABELS_FI = {
  ambitious: 'Kunnianhimoinen',
  feasible: 'Toteuttamiskelpoinen',
  innovative: 'Innovatiivinen',
  creative: 'Luova',
  research_backed: 'Tutkimuspohjainen',
  systemic: 'Systeeminen',
  tested_elsewhere: 'Kokeiltu muualla',
  quick_to_implement: 'Nopea toteuttaa',
  uncharted: 'Kartoittamaton alue',
  expert_insight: 'Ammattitaidon oivallus',
  bold_hypothesis: 'Rohkea hypoteesi',
};

/** English labels for AI prompts (en workshops). */
export const EVALUATION_TAG_LABELS_EN = {
  ambitious: 'Ambitious',
  feasible: 'Feasible',
  innovative: 'Innovative',
  creative: 'Creative',
  research_backed: 'Research-backed',
  systemic: 'Systemic',
  tested_elsewhere: 'Tried elsewhere',
  quick_to_implement: 'Quick to implement',
  uncharted: 'Uncharted territory',
  expert_insight: 'Expert insight',
  bold_hypothesis: 'Bold hypothesis',
};

/** Swedish labels for AI prompts (sv workshops). */
export const EVALUATION_TAG_LABELS_SV = {
  ambitious: 'Ambitiös',
  feasible: 'Genomförbar',
  innovative: 'Innovativ',
  creative: 'Kreativ',
  research_backed: 'Forskningsbaserad',
  systemic: 'Systemisk',
  tested_elsewhere: 'Prövad någon annanstans',
  quick_to_implement: 'Snabb att genomföra',
  uncharted: 'Okänt område',
  expert_insight: 'Expertinsikt',
  bold_hypothesis: 'Djärv hypotes',
};

function normalizePromptLang(language) {
  const c = String(language || 'fi').trim().toLowerCase();
  if (c === 'en' || c === 'eng' || c === 'english') return 'en';
  if (c === 'sv' || c === 'se' || c === 'swe' || c === 'swedish') return 'sv';
  return 'fi';
}

function evaluationTagLabelsForLang(language) {
  const lang = normalizePromptLang(language);
  if (lang === 'en') return EVALUATION_TAG_LABELS_EN;
  if (lang === 'sv') return EVALUATION_TAG_LABELS_SV;
  return EVALUATION_TAG_LABELS_FI;
}

/** Localized UI labels for evaluation tags (fi / en / sv). */
export function getEvaluationTagLabels(language = 'fi') {
  return evaluationTagLabelsForLang(language);
}

// A faithful translation of the Finnish earning rules every room was tagged with
// until #1371 (bench/legacyTagRules.js keeps the originals); keep the meaning in step (#1402).
const EARNING_RULES_EN = {
  ambitious: 'Proposes a goal clearly larger in scale; name the target/scale.',
  feasible: 'Describes implementation steps, resources or constraints so that it sounds realistic.',
  innovative: 'Presents a new way of operating or method; explain what is different.',
  creative: 'Combines themes/solutions in a way that departs from the usual; describe the creative twist.',
  research_backed: 'Backs the claim with research evidence: names sources or the study design.',
  systemic: 'Gets at systemic levers: incentives, decision-making, dependencies, feedback loops.',
  tested_elsewhere: 'Refers to implementation elsewhere or to results of a trial; name the context.',
  quick_to_implement: 'Allows a quick start: a short timeframe and few dependencies/steps.',
  uncharted: 'Names concretely something not yet tried in this workshop or region.',
  expert_insight: "Brings out an expert's insight or an experience-based, reasoned observation; name where the insight comes from.",
  bold_hypothesis: 'Formulates a bold hypothesis: a claim + the expected consequence and what is needed to test it.',
};

/**
 * Build a bullet list of allowed tag ids + names for LLM prompts. English for
 * every instance, like the instructions around it (#1371); the model writes its
 * reasoning in the instance language.
 */
export function formatEvaluationTagListForPrompt() {
  return EVALUATION_TAG_IDS.map((id) => `- "${id}" — ${EVALUATION_TAG_LABELS_EN[id] || id}: earns if ${EARNING_RULES_EN[id] || 'the input contains a concrete justification'}`).join('\n');
}

export function isValidEvaluationTagId(id) {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(EVALUATION_TAG_META, id);
}

/** JSON-schema fragment for one tag in tool_use schemas. */
export function evaluationTagItemSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'string', enum: [...EVALUATION_TAG_IDS] },
      reasoning: { type: 'string' },
    },
    required: ['id', 'reasoning'],
  };
}

export function contradictsResearchSchema() {
  return {
    type: 'object',
    properties: {
      reasoning: { type: 'string' },
      sources: { type: 'array', items: { type: 'number' } },
    },
    required: ['reasoning'],
  };
}

/** Explicit research relationship: supported | silent | contradicted */
export const RESEARCH_STANCE_IDS = ['supported', 'silent', 'contradicted'];

export function isValidResearchStance(id) {
  return typeof id === 'string' && RESEARCH_STANCE_IDS.includes(id);
}

export function researchStanceSchema() {
  return {
    type: 'string',
    enum: [...RESEARCH_STANCE_IDS],
    description:
      'supported = research excerpts back the core claim; silent = research does not address it (neutral, expected for expert/frontier ideas); contradicted = excerpts explicitly overturn the core claim',
  };
}

/**
 * @param {{ minItems?: number, maxItems?: number }} [opts]
 */
export function evaluationTagsOnlySchema({ minItems = 0, maxItems = 3 } = {}) {
  return {
    type: 'object',
    properties: {
      tags: {
        type: 'array',
        minItems,
        maxItems,
        items: evaluationTagItemSchema(),
      },
      research_stance: researchStanceSchema(),
      contradicts_research: contradictsResearchSchema(),
    },
    required: ['tags', 'research_stance'],
  };
}

/**
 * Normalize tag evaluation from AI output (tags or enrichTags key).
 * @returns {{ version: 2, tags: Array<{id:string,reasoning:string}>, research_stance: string|null, contradicts_research: object|null }}
 */
export function normalizeEvaluationTags(raw, { maxTags = 5 } = {}) {
  const sourceTags = raw?.tags || raw?.enrichTags || [];
  let tags = (Array.isArray(sourceTags) ? sourceTags : [])
    .filter((t) => t && isValidEvaluationTagId(t.id) && t.reasoning)
    .slice(0, maxTags);

  let research_stance = null;
  if (isValidResearchStance(raw?.research_stance)) {
    research_stance = raw.research_stance;
  } else if (raw?.contradicts_research?.reasoning) {
    research_stance = 'contradicted';
  }

  let contradicts_research = null;
  if (raw?.contradicts_research?.reasoning) {
    contradicts_research = {
      reasoning: String(raw.contradicts_research.reasoning),
      sources: Array.isArray(raw.contradicts_research.sources) ? raw.contradicts_research.sources : [],
    };
    research_stance = research_stance || 'contradicted';
  }

  // Silence is the default when research was consulted but neither supports nor contradicts
  if (!research_stance) research_stance = 'silent';

  return { version: 2, tags, research_stance, contradicts_research };
}

/** True if payload uses tag-based evaluation (not legacy numeric). */
export function hasTagEvaluation(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.version === 2 && Array.isArray(payload.tags)) return true;
  const tags = payload.tags || payload.enrichTags || payload.scores?.tags || payload.evaluation?.tags;
  return Array.isArray(tags) && tags.some((t) => t?.id && isValidEvaluationTagId(t.id));
}

/**
 * Read tag evaluation from node_analysis, rag_references.scores, or similar blobs.
 * `researchUnavailable` (a code: the research search failed, or the workshop has
 * no research datasets) is kept, so a view can say why there are no sources (#1421).
 * @returns {{ version: 2, tags: Array, contradicts_research: object|null, researchUnavailable?: string }|null}
 */
export function extractTagEvaluation(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.version === 2 && Array.isArray(payload.tags)) {
    return {
      version: 2,
      tags: payload.tags,
      research_stance: isValidResearchStance(payload.research_stance)
        ? payload.research_stance
        : (payload.contradicts_research?.reasoning ? 'contradicted' : 'silent'),
      contradicts_research: payload.contradicts_research || null,
      ...(payload.researchUnavailable ? { researchUnavailable: payload.researchUnavailable } : {}),
    };
  }
  if (Array.isArray(payload.tags) && payload.tags.length && !payload.impact?.score) {
    return normalizeEvaluationTags(payload, { maxTags: 5 });
  }
  if (payload.scores && hasTagEvaluation(payload.scores)) {
    return extractTagEvaluation(payload.scores);
  }
  if (payload.evaluation && hasTagEvaluation(payload.evaluation)) {
    return extractTagEvaluation(payload.evaluation);
  }
  return null;
}

/** Legacy numeric Tutkimustuki/Osuvuus still present. */
export function hasLegacyNumericScores(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (hasTagEvaluation(payload)) return false;
  const s = payload.scores || payload;
  return typeof s.research_support === 'number' || typeof s.quality === 'number';
}

const PROMPT_LANGUAGE_NAMES = { fi: 'Finnish', en: 'English', sv: 'Swedish' };

/**
 * Prompt block appended to user prompts that previously asked for numeric scores.
 * Instructions are English for every instance (#1371); only the reasoning
 * follows the instance language.
 * @param {{ maxTags?: number, language?: string }} [opts]
 */
export function evaluationTagsPromptRules({ maxTags = 3, language = 'fi' } = {}) {
  const countLabel = maxTags === 1 ? '0–1' : `0–${maxTags}`;
  return `

TAG-BASED EVALUATION (return tag objects only; tags may be empty):
- Select ${countLabel} tag(s) from the list (use the id field exactly):
${formatEvaluationTagListForPrompt()}
- Only return a tag if you can point to a concrete phrase in the participant input that justifies it.
- For each returned tag, the reasoning MUST include a short quoted phrase from the input that earned the tag.
- Write each reasoning in ${PROMPT_LANGUAGE_NAMES[normalizePromptLang(language)]}, quoting the input as it was written.
- Use research_backed only when the prompt includes concrete evidence or sources supporting the core claim.
- research_stance (required): exactly one of "supported" | "silent" | "contradicted".
  - supported = research excerpts back the core claim
  - silent = research does not address this claim (NEUTRAL — expected for expert or frontier ideas; never treat as a weakness; never invent novelty tags because of silence)
  - contradicted = excerpts explicitly overturn the core claim — then also fill contradicts_research
- contradicts_research only if research_stance is contradicted — otherwise omit.
- If the input is too vague, generic, gibberish, or off-topic to justify any tag, return:
  { "tags": [], "research_stance": "silent" }`;
}
